import { apiFetch, parseApiError } from 'app/javascript/services/api';
import { Belt } from 'app/javascript/services/belts';

export interface PublicProfile {
  id: number;
  name: string;
  nickname: string | null;
  slug: string;
  birthday: string | null;
  photo_url: string | null;
  bjj_profile: BjjProfile | null;
}

export interface BjjProfile {
  id: number;
  started_training_at: string | null;
  training_years: number | null;
  current_belt: Belt | null;
  belts: Belt[];
}

export interface ProfileSummary {
  id: number;
  name: string;
  nickname: string | null;
  slug: string;
  photo_url: string | null;
  current_belt: Belt | null;
}

export interface BlackBeltProfile {
  id: number;
  name: string;
  nickname: string | null;
  photo_url: string | null;
  slug: string;
}

export interface AttendanceHeatmapResponse {
  member_since: string;
  total_training_days: number;
  weekly_class_days: number;
  check_ins: Record<string, number>;
}

const PUBLIC_PROFILES_PATH = '/api/public/profiles';
const PUBLIC_FIGHTERS_PATH = '/api/public/bjj/fighters';

export const profilesService = {
  async list(): Promise<ProfileSummary[]> {
    const response = await fetch(PUBLIC_PROFILES_PATH);

    if (!response.ok) {
      throw new Error('Failed to fetch profiles');
    }

    return response.json();
  },

  async get(slug: string, companySlug?: string): Promise<PublicProfile> {
    const query = companySlug ? `?company_slug=${encodeURIComponent(companySlug)}` : '';
    const response = await fetch(`${PUBLIC_PROFILES_PATH}/${slug}${query}`);

    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }

    return response.json();
  },

  async getMyProfile(): Promise<PublicProfile> {
    const response = await apiFetch('/api/v1/me');

    if (!response.ok) {
      throw new Error('Failed to fetch my profile');
    }

    return response.json();
  },

  async updateMe(params: {
    name?: string;
    nickname?: string | null;
    birthday?: string | null;
    cpf?: string | null;
  }): Promise<void> {
    const response = await apiFetch('/api/v1/profile', {
      method: 'PATCH',
      body: JSON.stringify({ user: params }),
    });

    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Falha ao atualizar perfil'));
    }
  },

  async getCheckInHeatmap(slug: string, companySlug: string): Promise<AttendanceHeatmapResponse> {
    const response = await fetch(
      `${PUBLIC_PROFILES_PATH}/${slug}/attendance?company_slug=${companySlug}`
    );
    if (!response.ok) throw new Error('Failed to fetch attendance');
    return response.json();
  },

  async searchBlackBelts(query: string): Promise<BlackBeltProfile[]> {
    if (query.length < 3) {
      return [];
    }

    const response = await fetch(`${PUBLIC_FIGHTERS_PATH}?q=${encodeURIComponent(query)}`);

    if (!response.ok) {
      throw new Error('Failed to search black belts');
    }

    return response.json();
  },
};
